import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Award, GraduationCap, BookOpen, CheckCircle2, Clock, ArrowLeft, Download } from 'lucide-react';
import { Crest } from '@/components/Brand';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';

const SUB_BADGE = {
  approved: 'bg-trjgreen/10 text-trjgreen',
  submitted: 'bg-gold/10 text-gold',
  revision_required: 'bg-amber-100 text-amber-700',
  rejected: 'bg-red-100 text-red-700',
  superseded: 'bg-slate-100 text-slate-500',
};

export default function Transcript() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { api.get('/transcript/me').then(({ data }) => setData(data)); }, []);

  if (!data) return <div className="min-h-screen flex items-center justify-center"><div className="h-10 w-10 rounded-full border-2 border-navy/20 border-t-navy animate-spin" /></div>;

  const downloadCert = async (num) => {
    try {
      const res = await api.get(`/certificates/${num}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${num}.pdf`; document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error('Could not download'); }
  };

  return (
    <div className="min-h-screen bg-trjlight" data-testid="transcript-page">
      {/* Crest header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-10 flex items-center gap-6">
          <Crest size="xl" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-gold font-semibold">Official Academic Transcript</div>
            <h1 className="font-display font-semibold text-3xl text-navy mt-1">{data.student.name}</h1>
            <div className="text-sm text-slate-600">{data.student.email}</div>
            <div className="mt-2 text-xs text-slate-500 font-mono">Student ID: {data.student.user_id}</div>
          </div>
          <Link to="/dashboard" className="text-slate-500 hover:text-navy inline-flex items-center text-sm" data-testid="back-to-dashboard"><ArrowLeft className="h-4 w-4 mr-1" /> Dashboard</Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-10 space-y-10">
        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Enrolled" value={data.summary.courses_enrolled} icon={BookOpen} />
          <StatCard label="Completed" value={data.summary.courses_completed} icon={CheckCircle2} />
          <StatCard label="Certificates" value={data.summary.certificates_earned} icon={Award} accent />
          <StatCard label="Submissions" value={data.summary.submissions_total} icon={GraduationCap} />
        </div>

        {/* Courses */}
        <section>
          <h2 className="font-display font-semibold text-xl text-navy mb-4">Course history</h2>
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>{['Course','School','Enrolled','Status','Progress','Avg. Score'].map(h => <th key={h} className="px-4 py-3 text-xs uppercase tracking-widest text-slate-500 font-semibold">{h}</th>)}</tr>
              </thead>
              <tbody>
                {data.courses.length === 0 && <tr><td colSpan={6} className="text-center text-slate-500 py-12">No course history yet.</td></tr>}
                {data.courses.map(c => {
                  const pct = c.lessons_total + c.quizzes_total
                    ? Math.round(((c.lessons_done + c.quizzes_passed) / (c.lessons_total + c.quizzes_total)) * 100)
                    : 0;
                  return (
                    <tr key={c.course_id} className="border-t border-slate-100">
                      <td className="px-4 py-3 font-semibold text-navy">{c.course_title}</td>
                      <td className="px-4 py-3 text-slate-600">{c.school}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{new Date(c.enrolled_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3">{c.is_complete
                        ? <span className="text-trjgreen text-xs uppercase tracking-widest font-semibold inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Complete</span>
                        : <span className="text-gold text-xs uppercase tracking-widest font-semibold inline-flex items-center gap-1"><Clock className="h-3 w-3" /> In progress</span>}</td>
                      <td className="px-4 py-3 w-40"><Progress value={pct} className="h-2" /><div className="text-xs text-slate-500 mt-1">{pct}%</div></td>
                      <td className="px-4 py-3 text-navy font-serif text-base">{c.average_score_pct ?? '—'}{c.average_score_pct != null && '%'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* Certificates */}
        <section>
          <h2 className="font-display font-semibold text-xl text-navy mb-4">Certificates earned</h2>
          {data.certificates.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500">No certificates yet.</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {data.certificates.map(c => (
                <div key={c.certificate_number} className="bg-white border border-slate-200 rounded-xl p-5 flex items-start justify-between">
                  <div>
                    <Award className={`h-8 w-8 ${c.revoked ? 'text-red-400' : c.status === 'superseded' ? 'text-slate-400' : 'text-gold'}`} />
                    <div className="mt-2 text-xs uppercase tracking-widest text-gold font-semibold">{c.certificate_number}</div>
                    <div className="font-display font-semibold text-navy mt-1">{c.course_title}</div>
                    <div className="text-xs text-slate-500 mt-1">Issued {new Date(c.issued_at).toLocaleDateString()}</div>
                    {c.revoked && <div className="text-xs text-red-600 font-semibold mt-1">REVOKED</div>}
                    {c.status === 'superseded' && <div className="text-xs text-slate-500 font-semibold mt-1">Superseded</div>}
                  </div>
                  {!c.revoked && c.status !== 'superseded' && (
                    <Button size="sm" className="btn-navy" onClick={() => downloadCert(c.certificate_number)}><Download className="h-3.5 w-3.5" /></Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Submissions */}
        <section>
          <h2 className="font-display font-semibold text-xl text-navy mb-4">Submission history</h2>
          {data.submissions.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500">No submissions yet.</div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left">
                  <tr>{['Kind','Course','File','Submitted','Status','Grade','Feedback'].map(h => <th key={h} className="px-4 py-3 text-xs uppercase tracking-widest text-slate-500 font-semibold">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {data.submissions.map(s => (
                    <tr key={s.submission_id} className="border-t border-slate-100">
                      <td className="px-4 py-3"><span className="text-xs uppercase tracking-widest font-semibold bg-gold/10 text-gold px-2 py-1 rounded">{s.kind}</span></td>
                      <td className="px-4 py-3 text-slate-700">{s.course_title}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{s.filename}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{new Date(s.submitted_at).toLocaleString()}</td>
                      <td className="px-4 py-3"><span className={`text-xs uppercase tracking-widest font-semibold px-2 py-1 rounded ${SUB_BADGE[s.status] || ''}`}>{s.status.replace('_', ' ')}</span></td>
                      <td className="px-4 py-3 text-slate-700">{s.review?.grade || '—'}</td>
                      <td className="px-4 py-3 text-xs text-slate-600 max-w-xs truncate" title={s.review?.feedback || ''}>{s.review?.feedback || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <div className="border-t border-slate-200 pt-6 text-center font-serif italic text-slate-500">
          TRJ Academy™ — Knowledge • Integrity • Leadership
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className={`rounded-xl p-5 border ${accent ? 'bg-navy text-white border-navy' : 'bg-white border-slate-200'}`}>
      <div className="flex items-center justify-between">
        <div className={`text-xs uppercase tracking-widest ${accent ? 'text-gold' : 'text-slate-500'}`}>{label}</div>
        <Icon className={`h-4 w-4 ${accent ? 'text-gold' : 'text-navy'}`} />
      </div>
      <div className={`text-3xl font-display font-semibold mt-3 ${accent ? '' : 'text-navy'}`}>{value}</div>
    </div>
  );
}
